import {useEffect, useState} from "react";
import axios from "axios";
import {API_URL} from "../../../../config";
import {DENVER_TIME_ZONE, isCalendarDate, monthDate, monthKey} from "./rsoForecastWindowHelpers";
import {isSignInExpired} from "./RsoSignInExpired";

// One month's RSO input window as NANCY resolves it: the dates an admin set for that input month
// (configured) or the standard rule (the last 7 days of the month before it). Denver calendar dates.
//   GET  Dashboard/api/RsoInputWindow[?inputMonth=YYYY-MM-01]          (anyone with RSO access; no month = current input month)
//   POST Dashboard/api/RsoInputWindow       {inputMonth, opensOn, closesOn, expectedLastChangeId}   (manage-rso-input-window)
//   POST Dashboard/api/RsoInputWindow/clear {inputMonth, expectedLastChangeId}                      (manage-rso-input-window)
// Each answers {inputMonth, opensOn, closesOn, configured, canEdit, editableMonths,
//   lastChange: {id, action, changedByUserId, changedByName, changedOnUtc} | null}  (RsoInputWindowChangeItem).
// expectedLastChangeId is the lastChange.id the admin was looking at (0: never changed); if someone changed the
// month since, NANCY refuses with 409 {message, code: "input_window_changed"}. A refused save is 400 {message},
// a missing permission 403.
export const INPUT_WINDOW_ENDPOINT = `${API_URL}/Dashboard/api/RsoInputWindow`;

const isRecord = (value) => value !== null && typeof value === "object" && !Array.isArray(value);
// "2026-10-25" (also accepts a serialized midnight such as "2026-10-25T00:00:00").
const calendarDate = (value) => {
  const match = /^(\d{4}-\d{2}-\d{2})(?:T00:00(?::00(?:\.0+)?)?(?:Z|[+-]00:?00)?)?$/.exec(String(value ?? "").trim());
  return match && isCalendarDate(match[1]) ? match[1] : "";
};

// Anything incomplete, or for another month than the one asked for, is null (never shown).
export const normalizeInputWindow = (data, inputMonth) => {
  if (!isRecord(data)) return null;
  const month = monthKey(calendarDate(data.inputMonth) || data.inputMonth);
  const opensOn = calendarDate(data.opensOn);
  const closesOn = calendarDate(data.closesOn);
  if (!month || (inputMonth && month !== monthKey(inputMonth)) || !opensOn || !closesOn) return null;
  const change = isRecord(data.lastChange) ? data.lastChange : null;
  // The id is kept even when the rest can't be shown: it is what a save or clear must send back.
  const lastChangeId = Number.isSafeInteger(change?.id) && change.id > 0 ? change.id : 0;
  const changedByName = typeof change?.changedByName === "string" ? change.changedByName.trim() : "";
  const changedOnUtc = typeof change?.changedOnUtc === "string" && !Number.isNaN(Date.parse(change.changedOnUtc))
    ? change.changedOnUtc : "";
  // The months NANCY lets an admin change now ("2026-11"), in NANCY's order.
  const editableMonths = (Array.isArray(data.editableMonths) ? data.editableMonths : [])
    .map((value) => monthKey(calendarDate(value) || value)).filter(Boolean);
  return {inputMonth: monthDate(month), opensOn, closesOn, configured: data.configured === true, canEdit: data.canEdit === true,
    editableMonths, lastChangeId, lastChange: lastChangeId && changedOnUtc ? {id: lastChangeId, changedByName, changedOnUtc} : null};
};

// "Tue Oct 6, 2026, 3:15 PM Mountain Time"
export const changedOnText = (onUtc) => {
  const at = new Date(onUtc);
  if (Number.isNaN(at.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-US", {timeZone: DENVER_TIME_ZONE, weekday: "short", month: "short", day: "numeric",
    year: "numeric", hour: "numeric", minute: "2-digit", hour12: true}).formatToParts(at);
  const part = (type) => parts.find((item) => item.type === type)?.value || "";
  return `${part("weekday")} ${part("month")} ${part("day")}, ${part("year")}, ${part("hour")}:${part("minute")} `
    + `${part("dayPeriod").toUpperCase()} Mountain Time`;
};

const DAY_MS = 86400000;
const dayNumber = (value) => Date.parse(`${value}T00:00:00Z`) / DAY_MS;
// The checks any screen can make before sending. NANCY checks these again, and the rest (which dates and
// months are allowed); its message is shown as it is.
export const inputWindowProblem = ({opensOn, closesOn}) => {
  if (!isCalendarDate(opensOn) || !isCalendarDate(closesOn)) return "Enter the date the window opens and the date it closes.";
  if (opensOn > closesOn) return "The window can't close before it opens.";
  if (dayNumber(closesOn) - dayNumber(opensOn) + 1 > 31) return "The window can be at most 31 days long.";
  return "";
};

// A refused read or save in words. signInExpired is shown with NANCY's sign-in message instead.
export const describeInputWindowFailure = (failure, fallback) => {
  const status = failure?.response?.status;
  const message = failure?.response?.data?.message;
  if (isSignInExpired(failure)) return {signInExpired: true, forbidden: false, conflict: false, message: ""};
  if (status === 403) return {signInExpired: false, forbidden: true, conflict: false, message: "Your account can't set the RSO input window."};
  // conflict: someone else changed the month since it was opened (the caller reloads it).
  if (status === 409) {
    return {signInExpired: false, forbidden: false, conflict: true, message: typeof message === "string" && message.trim() ? message.trim()
      : "Someone else changed this input window since you opened it. It has been reloaded: check it and try again."};
  }
  if (status === 400 && typeof message === "string" && message.trim()) return {signInExpired: false, forbidden: false, conflict: false, message: message.trim()};
  return {signInExpired: false, forbidden: false, conflict: false, message: fallback};
};

const headers = (token) => ({Authorization: `Bearer ${token}`});
const checked = (response, inputMonth) => {
  const value = normalizeInputWindow(response?.data, inputMonth);
  if (!value) throw new Error("Invalid RSO input window");
  return value;
};

// Without a month NANCY answers for its current input month (the first whose window closes today or later).
export const readInputWindow = async (token, inputMonth) => checked(await axios.get(INPUT_WINDOW_ENDPOINT,
  {headers: headers(token), params: inputMonth ? {inputMonth: monthDate(inputMonth)} : {}, timeout: 30000}), inputMonth);
const expectedId = (value) => (Number.isSafeInteger(value) && value > 0 ? value : 0);
// expectedLastChangeId: the lastChangeId of the read the admin is looking at (0 when the month was never changed).
export const saveInputWindow = async (token, {inputMonth, opensOn, closesOn, expectedLastChangeId}) => checked(await axios.post(
  INPUT_WINDOW_ENDPOINT, {inputMonth: monthDate(inputMonth), opensOn, closesOn, expectedLastChangeId: expectedId(expectedLastChangeId)},
  {headers: headers(token), timeout: 30000}), inputMonth);
export const clearInputWindow = async (token, inputMonth, expectedLastChangeId) => checked(await axios.post(`${INPUT_WINDOW_ENDPOINT}/clear`,
  {inputMonth: monthDate(inputMonth), expectedLastChangeId: expectedId(expectedLastChangeId)}, {headers: headers(token), timeout: 30000}), inputMonth);

// A read-only RSO input window for one month (null while off, loading or unreadable: callers then use the standard rule).
// refreshKey: re-read when it changes (for example the input-week version, so an admin's change shows on the next refresh).
export default function useRsoInputWindow({token, inputMonth, enabled = true, refreshKey}) {
  const month = monthKey(inputMonth);
  const active = enabled && !!token && !!month;
  const [result, setResult] = useState({key: "", value: null});
  const key = `${token}|${month}`;
  useEffect(() => {
    if (!active) return undefined;
    let current = true;
    readInputWindow(token, month).then((value) => {if (current) setResult({key, value});})
      .catch(() => {if (current) setResult((previous) => (previous.key === key ? previous : {key, value: null}));});
    return () => {current = false;};
  }, [active, token, month, key, refreshKey]);
  return active && result.key === key ? result.value : null;
}
